/* eslint-disable @typescript-eslint/naming-convention */

import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Menu,
  Stack,
} from '@mui/material';
import {
  useDisableEstablishment,
  useEnableEstablishment,
} from '@establishment/mutations/establishmentMutations';
import { useEffect, useState } from 'react';

import { AxiosResponse } from 'axios';
import { DialogContentStyled } from '@styled/dialog-content-styled';
import EstablishmentItem from '@establishment/models/dto/establishment-item.response.dto';
import { JButton } from '@atoms/JButton';
import JTableCell from '@atoms/JTabelCell';
import { MenuItemStyled } from '@styled/menu-item.styled';
import Paths from '@/routes/paths';
import { ShowMoreButton } from '@atoms/ShowMoreButton';
import { StatusChip } from '@atoms/StatusChip';
import { TableRowStyled } from '@styled/table-row.styled';
import { UseMutateFunction } from '@tanstack/react-query';
import useJDialog from '@/hooks/useJDialog';
import useJSnackbar from '@/hooks/useJSnackbar';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Props {
  row: EstablishmentItem;
}
type disbaleMutationType = UseMutateFunction<
  AxiosResponse<null, any>,
  unknown,
  { id: string; sendEmail: boolean },
  unknown
>;

type enableMutationType = UseMutateFunction<
  AxiosResponse<null, any>,
  unknown,
  { id: string },
  unknown
>;

const EstablishmentRow = ({ row }: Props) => {
  const { t } = useTranslation();

  const {
    establishment_name,
    establishment_siret,
    city_name,
    city_zip_code,
    enterprise_name,
    userscount,
    address_city_name,
    establishment_id,
    establishment_deleted_at,
  } = row;

  const [rowMenuanchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [isActive, setIsActive] = useState<boolean>();

  const navigate = useNavigate();
  const {
    mutate: enableEstablishmentMutate,
    isSuccess: enableSuccess,
    isError: enableError,
  } = useEnableEstablishment();
  const {
    mutate: disableEstablishmentMutate,
    isSuccess: disableSuccess,
    isError: disableError,
  } = useDisableEstablishment();
  const open = Boolean(rowMenuanchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRowMenuAnchorEl(event.currentTarget);
  };
  const handleRowMenuClose = () => {
    setRowMenuAnchorEl(null);
  };

  const { showJDialog, closeJDialog } = useJDialog();
  const { showSnackbar } = useJSnackbar();

  const openDisableDialog = (
    establishmentName: string,
    enterpriseId: string,
    disableMutation: disbaleMutationType,
  ): void => {
    handleRowMenuClose();
    showJDialog(
      t('DISABLE_ESTABLISHMENT'),
      <DisableDialogContent
        establishmentId={enterpriseId}
        establishmentName={establishmentName}
        disableMutation={disableMutation}
      />,
    );
  };
  const openEnableDialog = (
    establishmentName: string,
    enterpriseId: string,
    enableMutation: enableMutationType,
  ): void => {
    handleRowMenuClose();
    showJDialog(
      t('ENABLE_ESTABLISHMENT'),
      <EnableDialogContent
        establishmentId={enterpriseId}
        establishmentName={establishmentName}
        enableMutation={enableMutation}
      />,
    );
  };
  const handleChangeStatus = (isStatusActive: boolean) => {
    isStatusActive
      ? openDisableDialog(
          `${row.establishment_name}`,
          establishment_id,
          disableEstablishmentMutate,
        )
      : openEnableDialog(
          `${row.establishment_name}`,
          establishment_id,
          enableEstablishmentMutate,
        );
  };
  useEffect(() => {
    setIsActive(!establishment_deleted_at);
  }, [establishment_deleted_at]);
  useEffect(() => {
    if (!enableSuccess) return;
    setIsActive(enableSuccess);
    closeJDialog();
  }, [enableSuccess]);
  useEffect(() => {
    if (!disableSuccess) return;
    setIsActive(!disableSuccess);
    closeJDialog();
  }, [disableSuccess]);

  useEffect(() => {
    if (enableError) showSnackbar(t('API_ERROR'), 'error');
  }, [enableError]);
  useEffect(() => {
    if (disableError) showSnackbar(t('API_ERROR'), 'error');
  }, [disableError]);

  useEffect(() => {
    setIsActive(!establishment_deleted_at);
  }, [establishment_deleted_at]);
  return (
    <TableRowStyled
      key={establishment_id}
      data-test={`establishment-list-row-${establishment_siret}`}
    >
      <JTableCell isbold>{establishment_name}</JTableCell>
      <JTableCell>{establishment_siret}</JTableCell>
      <JTableCell>{city_zip_code}</JTableCell>
      <JTableCell>{city_name ?? address_city_name}</JTableCell>
      <JTableCell>
        <StatusChip
          label={isActive ? t('ACTIVE') : t('INACTIVE')}
          variant={isActive ? 'success' : 'danger'}
        ></StatusChip>
      </JTableCell>
      <JTableCell>{enterprise_name}</JTableCell>
      <JTableCell>{userscount}</JTableCell>

      <JTableCell>
        <ShowMoreButton onClick={handleClick} open={open} />
        <Menu
          id="basic-menu"
          anchorEl={rowMenuanchorEl}
          open={open}
          onClose={handleRowMenuClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItemStyled
            data-qa="close"
            red={!!isActive}
            green={!isActive}
            onClick={() => handleChangeStatus(!!isActive)}
          >
            {isActive ? t('DESACTIVATE') : t('ACTIVATE')}
          </MenuItemStyled>
          {isActive && (
            <MenuItemStyled
              data-qa={`action-${establishment_siret}`}
              onClick={() =>
                navigate(
                  Paths.EditEstablishment.replace(':id', establishment_id),
                )
              }
              red={false}
              green={false}
            >
              {t('ESTABLISHMENT_DETAILS')}
            </MenuItemStyled>
          )}
        </Menu>
      </JTableCell>
    </TableRowStyled>
  );
};

const DisableDialogContent = ({
  establishmentName,
  establishmentId,
  disableMutation,
}: {
  establishmentName: string;
  establishmentId: string;
  disableMutation: disbaleMutationType;
}) => {
  const { closeJDialog } = useJDialog();
  const { t } = useTranslation();
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  console.log('sendEmail', sendEmail);
  return (
    <Stack gap={2}>
      <DialogContentStyled>
        {t('DISABLE_ESTABLISHMENT_TEXT')}
        <strong> {establishmentName}</strong> ?
      </DialogContentStyled>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox onChange={(e) => setSendEmail(e.target.checked)} />
          }
          label={t('DISABLE_ESTABLISHMENT_OPTION_2')}
        />
      </FormGroup>
      <Stack direction={'row'} gap={1}>
        <JButton
          onClick={() => disableMutation({ id: establishmentId, sendEmail })}
          variant="contained"
          color="error"
        >
          {t('DISABLE_ESTABLISHMENT_DISABLE_BUTTON')}
        </JButton>
        <JButton onClick={closeJDialog} variant="contained" color="info">
          Annuler
        </JButton>
      </Stack>
    </Stack>
  );
};

const EnableDialogContent = ({
  establishmentName,
  establishmentId,
  enableMutation,
}: {
  establishmentName: string;
  establishmentId: string;
  enableMutation: enableMutationType;
}) => {
  const { closeJDialog } = useJDialog();
  const { t } = useTranslation();
  return (
    <Stack gap={2}>
      <DialogContentStyled>
        {t('DISABLE_ESTABLISHMENT_TEXT')}
        <strong> {establishmentName}</strong> ?
      </DialogContentStyled>

      <Stack direction={'row'} gap={1}>
        <JButton
          data-qa="enable"
          onClick={() => enableMutation({ id: establishmentId })}
          variant="contained"
          color="secondary"
        >
          {t('ENABLE_ESTABLISHMENT_DISABLE_BUTTON')}
        </JButton>
        <JButton
          data-qa="close"
          onClick={closeJDialog}
          variant="contained"
          color="info"
        >
          {t('CANCEL')}
        </JButton>
      </Stack>
    </Stack>
  );
};

export default EstablishmentRow;
